import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ForwardedRef,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
  type TransitionEvent as ReactTransitionEvent,
} from "react";

import { ChevronLeft, ChevronRight, PlayCircle } from "@mui/icons-material";
import { Box } from "@mui/material";
import type { Breakpoint } from "@mui/material/styles";

export interface MediaGalleryItem {
  /** "image" renders an <img>; "video" paints a poster frame from the file. */
  type: "image" | "video";
  /** Primary source. For images the thumbnail; for videos the playable file. */
  src: string;
  /**
   * Shown on the hero instead of `src` — the full-resolution original where `src` is a small
   * derivative. Only the photos around the hero are mounted.
   */
  heroSrc?: string;
  /** Image-only: used if the source fails to load (e.g. a missing thumbnail). */
  fallbackSrc?: string;
  /**
   * A short identifier for this one photo, shown in a chip in its corner. What names a photo is the
   * caller's business; the gallery only promises that the same mark is on the photo and its
   * thumbnail, so something elsewhere on the page can point at one.
   */
  label?: string;
  /**
   * Laid over the bottom of the hero while this item is showing. Free-form so the gallery stays
   * free of any domain vocabulary. A function is called with whether the hero is holding more than
   * one photo, so the caller can shrink what it shows.
   */
  caption?: ReactNode | ((dense: boolean) => ReactNode);
}

/** What a gallery falls back to when the caller sets no `height`. */
const DEFAULT_HEIGHT = 320;
/** Measured on the gallery, not the viewport: it sits in a panel the reader can drag narrower. */
const NARROW_WIDTH = 480;
const NARROW_TILES = 4;
const HERO_GAP = 2;
const TILE_GAP = 4;
const TILE_HEIGHT = 56;
const DRAG_SLOP = 5;
const SWIPE_COMMIT = 40;
const WHEEL_STEP = 40;
const ACTIVE_BORDER = "2px solid rgba(0,0,0,0.7)";
const IDLE_BORDER = "1px solid rgba(0,0,0,0.12)";
const HOVER_BORDER = "1px solid rgba(0,0,0,0.4)";

/** A plain height, or one per breakpoint — what `sx`'s `height` already accepts. */
export type MediaGalleryHeight =
  | number
  | string
  | Partial<Record<Breakpoint, number | string>>;

export interface MediaGalleryProps {
  /** Shown on the hero and, where more of them than the hero holds, the thumbnail strip. */
  media?: MediaGalleryItem[];
  /** Height of the whole gallery — hero and strip together. */
  height?: MediaGalleryHeight;
  /** Photos side by side on the hero. Falls back to 1 under `NARROW_WIDTH`. */
  cluster?: number;
  /** Thumbnails visible at once. Defaults to 3. */
  slidesPerView?: number;
  /** Absolutely-positioned nodes layered over the hero (badges, buttons). */
  overlay?: ReactNode;
  /** Shown in place of the hero when `media` is empty. */
  emptyContent?: ReactNode;
  /**
   * What a click on a photo means, with its index. Left out entirely rather than built in: a card
   * opens the record behind it, a detail page opens the photo.
   */
  onHeroClick?: (index: number) => void;
}

export interface MediaGalleryHandle {
  /** Put a photo on the hero from outside, and bring the gallery back into view with it. */
  show: (index: number) => void;
}

/** Reveal once the element nears the viewport, so media loads lazily. */
function useInView(ref: RefObject<HTMLElement>) {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || inView) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, inView]);

  return inView;
}

function useWidth(ref: RefObject<HTMLElement>) {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.round(entry.contentRect.width)),
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    setReduced(
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
    );
  }, []);

  return reduced;
}

const playIconSx = (fontSize: number) =>
  ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontSize,
    color: "common.white",
    opacity: 0.9,
    pointerEvents: "none",
    filter: `drop-shadow(0 1px ${fontSize > 24 ? 4 : 2}px rgba(0,0,0,0.6))`,
  }) as const;

const chevronSx = (side: "left" | "right", opacity: number) =>
  ({
    position: "absolute",
    [side]: 4,
    top: "50%",
    mt: "-18px",
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    color: "common.white",
    opacity,
    transition: "opacity .15s ease",
    cursor: "pointer",
    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
    "&:hover": { opacity: 1 },
  }) as const;

const labelSx = (small: boolean) =>
  ({
    position: "absolute",
    top: small ? "4px" : "8px",
    left: small ? "4px" : "8px",
    maxWidth: small ? "calc(100% - 8px)" : "calc(100% - 16px)",
    minWidth: small ? 16 : 20,
    height: small ? 16 : 20,
    px: small ? "4px" : "5px",
    borderRadius: 999,
    bgcolor: "rgba(0,0,0,0.68)",
    color: "common.white",
    fontSize: 11,
    fontWeight: 600,
    lineHeight: small ? "16px" : "20px",
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    userSelect: "none",
    pointerEvents: "none",
  }) as const;

const arrowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: TILE_HEIGHT,
  flexShrink: 0,
  color: "rgba(0,0,0,0.54)",
  cursor: "pointer",
  borderRadius: 1,
  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
} as const;

/**
 * One to three photos at a time, with a thumbnail strip under them.
 *
 * Nothing is cropped: each frame is filled with a blurred copy of the same image, so a portrait
 * photo does not sit in a black box. Stepping slides the row along by one photo — the leading photo
 * leaves and the next arrives — by chevron, drag, or a horizontal (or shift +) wheel over the hero,
 * and wraps at either end. The strip is a rail in photo order where the photos on the hero collapse
 * into a single tile; clicking a tile ahead of them brings it in at the trailing edge, one behind at
 * the leading edge. Videos paint a poster frame straight from the file (the `#t` fragment seeks and
 * renders it); a play glyph marks them. Everything loads lazily, once the gallery nears the
 * viewport.
 *
 * Presentational: it owns no domain logic. Callers map their data into `media` and say what a click
 * means.
 */
function MediaGalleryView(
  {
    media = [],
    height,
    cluster = 1,
    slidesPerView = 3,
    overlay,
    emptyContent,
    onHeroClick,
  }: MediaGalleryProps,
  handle: ForwardedRef<MediaGalleryHandle>,
) {
  const rootRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const dragFrom = useRef<number | null>(null);
  const dragged = useRef(false);
  const wheel = useRef({ acc: 0, locked: false });

  const [win, setWin] = useState(0);
  const [dragDx, setDragDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [failedSrcs, setFailedSrcs] = useState<Record<string, boolean>>({});

  const inView = useInView(rootRef);
  const width = useWidth(rootRef);
  const reduceMotion = useReducedMotion();

  const count = media.length;
  const hasMedia = count > 0;
  const narrow = width !== null && width < NARROW_WIDTH;
  const perView = Math.max(1, Math.min(narrow ? 1 : cluster, count || 1));
  const tilesVisible = narrow
    ? Math.min(slidesPerView, NARROW_TILES)
    : slidesPerView;
  const hasStrip = count > perView;

  const wrap = (value: number) => ((value % count) + count) % count;
  const active = hasMedia ? wrap(win) : 0;
  // The position is left unwrapped for one step past either end, so a slide that wraps keeps the
  // direction it was heading; `snapTrack` folds it back once the slide lands.
  // A set that fits on the hero never slides: without clones either side, a position left over
  // from a narrower panel would shift the track onto empty space.
  const pos = !hasStrip
    ? 0
    : hasMedia && win >= -perView && win <= count + perView - 1
      ? win
      : active;

  const stepBy = useCallback(
    (delta: number) => {
      if (count <= perView) return;
      setSnapping(false);
      setWin((current) => {
        const inRange = (value: number) =>
          value >= -perView && value <= count + perView - 1;
        const from = inRange(current)
          ? current
          : ((current % count) + count) % count;
        const next = from + delta;
        return inRange(next) ? next : ((next % count) + count) % count;
      });
    },
    [count, perView],
  );

  const select = (index: number) => {
    // Every photo is already on the hero: there is nothing to bring to it.
    if (!hasStrip) return;
    // A thumbnail far down the strip is a jump, not a step: sliding through the photos between the
    // two would race past a dozen frames that have not been loaded to be seen.
    if (Math.abs(index - pos) > perView) {
      setSnapping(true);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setSnapping(false)),
      );
    }
    setWin(index);
  };

  useImperativeHandle(handle, () => ({
    show: (index: number) => {
      if (index < 0 || index >= count) return;
      select(index);
      // A chip clicked far down the bulletin would otherwise change a photo the reader cannot see.
      rootRef.current?.scrollIntoView?.({
        block: "nearest",
        behavior: "smooth",
      });
    },
  }));

  const snapTrack = (event: ReactTransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (!hasMedia || (win >= 0 && win < count)) return;
    setSnapping(true);
    setWin(wrap(win));
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setSnapping(false)),
    );
  };

  /** Bring the tile that just became active into the strip's view, if it has fallen out of it. */
  useEffect(() => {
    const rail = stripRef.current;
    if (!rail) return;
    const frame = requestAnimationFrame(() => {
      const tile = rail.querySelector<HTMLElement>("[data-active-tile]");
      if (!tile) return;
      const left = tile.offsetLeft;
      const right = left + tile.offsetWidth;
      // Centring a tile the reader has just clicked would slide the rail out from under them.
      if (
        left >= rail.scrollLeft &&
        right <= rail.scrollLeft + rail.clientWidth
      )
        return;
      rail.scrollTo({
        left: Math.max(0, left - (rail.clientWidth - tile.offsetWidth) / 2),
        behavior: "smooth",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [active]);

  /**
   * Horizontal trackpad scroll, or shift + wheel, over the hero. Deltas add up to one step, then a
   * short lock soaks up the momentum tail so a single flick moves one photo. Non-passive, so a
   * horizontal scroll can be kept from the page.
   */
  useEffect(() => {
    const node = heroRef.current;
    if (!node || count <= perView) return;
    let idle: ReturnType<typeof setTimeout>;
    let lock: ReturnType<typeof setTimeout>;
    const onWheel = (event: WheelEvent) => {
      const dx =
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : event.shiftKey
            ? event.deltaY
            : 0;
      if (!dx) return;
      event.preventDefault();
      if (wheel.current.locked) return;
      wheel.current.acc += dx;
      clearTimeout(idle);
      idle = setTimeout(() => {
        wheel.current.acc = 0;
      }, 150);
      if (Math.abs(wheel.current.acc) < WHEEL_STEP) return;
      stepBy(wheel.current.acc > 0 ? 1 : -1);
      wheel.current.acc = 0;
      wheel.current.locked = true;
      lock = setTimeout(() => {
        wheel.current.locked = false;
      }, 450);
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      node.removeEventListener("wheel", onWheel);
      clearTimeout(idle);
      clearTimeout(lock);
    };
  }, [count, perView, stepBy]);

  /** The strip takes the wheel too, vertical deltas included: the rail only runs sideways. */
  useEffect(() => {
    const rail = stripRef.current;
    if (!rail) return;
    const onWheel = (event: WheelEvent) => {
      const delta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : event.deltaY;
      if (!delta) return;
      const end = rail.scrollWidth - rail.clientWidth;
      // At either end the page has it, rather than the strip swallowing the scroll.
      if (delta < 0 ? rail.scrollLeft <= 0 : rail.scrollLeft >= end - 1) return;
      event.preventDefault();
      rail.scrollLeft += delta;
    };
    rail.addEventListener("wheel", onWheel, { passive: false });
    return () => rail.removeEventListener("wheel", onWheel);
  }, [hasStrip]);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (count <= perView) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    dragFrom.current = event.clientX;
    dragged.current = false;
    setDragging(true);
    setDragDx(0);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragFrom.current === null) return;
    const dx = event.clientX - dragFrom.current;
    if (!dragged.current) {
      if (Math.abs(dx) <= DRAG_SLOP) return;
      dragged.current = true;
      // Captured once it is a drag rather than on the press: a held capture sends the click that
      // follows to the hero, and the photo under the pointer never hears it.
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    setDragDx(dx);
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragFrom.current === null) return;
    const dx = event.clientX - dragFrom.current;
    dragFrom.current = null;
    setDragging(false);
    setDragDx(0);
    if (Math.abs(dx) > SWIPE_COMMIT) stepBy(dx < 0 ? 1 : -1);
  };

  const markFailed = (src: string) =>
    setFailedSrcs((prev) => ({ ...prev, [src]: true }));
  const srcFor = (item: MediaGalleryItem, preferred: string) =>
    failedSrcs[preferred] && item.fallbackSrc ? item.fallbackSrc : preferred;

  const renderMedia = (index: number, hero: boolean, load: boolean) => {
    const item = media[index];
    const source = hero ? (item.heroSrc ?? item.src) : item.src;
    const src = load ? srcFor(item, source) : undefined;

    if (item.type === "video") {
      return (
        <Box
          component="video"
          src={src ? `${src}#t=0.1` : undefined}
          preload="metadata"
          muted
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            pointerEvents: "none",
          }}
        />
      );
    }

    return (
      <>
        {/* Blurred backdrop fills the letterboxed area. */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: src ? `url(${src})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            // Darkened as well as blurred, so the photo is what the eye lands on rather than the
            // spread of its own colour behind it.
            filter: `blur(${hero ? 24 : 8}px) brightness(0.45) saturate(0.6)`,
            transform: "scale(1.15)",
          }}
        />
        <Box
          component="img"
          src={src}
          alt=""
          draggable={false}
          onError={() => markFailed(source)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      </>
    );
  };

  // Every photo sits on one track and the track slides, so a step reads as the row moving along by
  // one rather than a swap. Clones either side fill a position that has run past an end; a set that
  // fits on the hero never steps, so it is mounted once.
  const lead = hasStrip ? perView : 0;
  const slots: number[] = [];
  if (hasMedia) {
    const last = hasStrip ? count + 2 * perView - 1 : count;
    for (let k = -lead; k < last; k++) slots.push(k);
  }
  const heroBasis = `calc((100% - ${(perView - 1) * HERO_GAP}px) / ${perView})`;
  const tileBasis = `calc((100% - ${(tilesVisible - 1) * TILE_GAP}px) / ${tilesVisible})`;

  const tiles: { key: string; indices: number[]; select: () => void }[] = [];
  if (hasStrip && perView === 1) {
    for (let i = 0; i < count; i++) {
      tiles.push({ key: `t${i}`, indices: [i], select: () => select(i) });
    }
  } else if (hasStrip) {
    const onHero = Array.from({ length: perView }, (_, k) => wrap(active + k));
    for (let i = 0; i < count; i++) {
      if (i === active) {
        tiles.push({ key: "hero", indices: onHero, select: () => {} });
      } else if (!onHero.includes(i)) {
        // Read in strip order: a photo later in the set joins at the trailing edge, so the next one
        // in line turns A B C into B C D; an earlier one joins at the leading edge.
        tiles.push({
          key: `t${i}`,
          indices: [i],
          select: () => select(i > active ? i - perView + 1 : i),
        });
      }
    }
  }
  const activeTile = perView === 1 ? `t${active}` : "hero";
  const stripScrolls = tiles.length > tilesVisible;

  const scrollStrip = (direction: number) => {
    const rail = stripRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: direction * Math.max(rail.clientWidth - 72, 120),
      behavior: "smooth",
    });
  };

  return (
    // `height` defaults rather than being required: the hero fills what the strip leaves of it, so a
    // caller that omits it would otherwise get a gallery collapsed to nothing.
    <Box
      ref={rootRef}
      sx={{
        height: height ?? DEFAULT_HEIGHT,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Hero */}
      <Box
        ref={heroRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          borderRadius: 1,
          touchAction: "pan-y",
          userSelect: "none",
          // Each photo paints its own black frame, so all this shows is the seam between two of
          // them on the hero — which reads as a gap rather than a black bar.
          bgcolor: "background.paper",
          cursor: hasStrip ? (dragging ? "grabbing" : "grab") : "default",
        }}
      >
        {hasMedia ? (
          <Box
            onTransitionEnd={snapTrack}
            sx={{
              display: "flex",
              height: "100%",
              gap: `${HERO_GAP}px`,
              transform: `translateX(calc(${-(pos + lead)} * (${heroBasis} + ${HERO_GAP}px) + ${dragDx}px))`,
              transition:
                dragging || snapping || reduceMotion
                  ? "none"
                  : "transform 0.35s cubic-bezier(0.2, 0, 0, 1)",
              willChange: "transform",
            }}
          >
            {slots.map((slot) => {
              const index = wrap(slot);
              const item = media[index];
              const caption =
                typeof item.caption === "function"
                  ? item.caption(perView > 1)
                  : item.caption;
              const inset = perView === 1 ? "8px" : "5px";
              const onScreen = slot >= pos && slot < pos + perView;
              return (
                <Box
                  key={slot}
                  // A clone repeats a photo that is already on the track, and is read out twice.
                  aria-hidden={onScreen ? undefined : true}
                  onClick={() => {
                    if (!dragged.current) onHeroClick?.(index);
                  }}
                  sx={{
                    position: "relative",
                    flex: `0 0 ${heroBasis}`,
                    height: "100%",
                    overflow: "hidden",
                    borderRadius: 1,
                    bgcolor: "common.black",
                    cursor: onHeroClick ? "pointer" : "inherit",
                  }}
                >
                  {renderMedia(
                    index,
                    true,
                    inView &&
                      slot >= pos - perView &&
                      slot <= pos + 2 * perView - 1,
                  )}
                  {item.type === "video" && <PlayCircle sx={playIconSx(56)} />}
                  {item.label && <Box sx={labelSx(false)}>{item.label}</Box>}
                  {caption && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: inset,
                        right: inset,
                        bottom: inset,
                        // A label, not a target: a click on it reaches the photo underneath.
                        pointerEvents: "none",
                      }}
                    >
                      {caption}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : (
          emptyContent
        )}

        {hasStrip && (
          <>
            <Box
              onClick={() => stepBy(-1)}
              sx={chevronSx("left", hovering ? 1 : 0.4)}
            >
              <ChevronLeft />
            </Box>
            <Box
              onClick={() => stepBy(1)}
              sx={chevronSx("right", hovering ? 1 : 0.4)}
            >
              <ChevronRight />
            </Box>
          </>
        )}

        {overlay}
      </Box>

      {/* Thumbnail strip */}
      {hasStrip && (
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{ py: 1, display: "flex", alignItems: "center", gap: "4px" }}
        >
          <Box
            onClick={() => (stripScrolls ? scrollStrip(-1) : stepBy(-1))}
            sx={arrowSx}
          >
            <ChevronLeft fontSize="small" />
          </Box>
          <Box
            ref={stripRef}
            sx={{
              // Positioned so a tile's `offsetLeft` is measured against the rail it scrolls in.
              position: "relative",
              flex: 1,
              minWidth: 0,
              display: "flex",
              gap: `${TILE_GAP}px`,
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              py: "2px",
            }}
          >
            {tiles.map((tile) => {
              const isActive = tile.key === activeTile;
              const item = media[tile.indices[0]];
              // The tile that holds the photos on the hero carries all of their marks.
              const tileLabel = tile.indices
                .map((index) => media[index].label)
                .filter(Boolean)
                .join(", ");
              return (
                <Box
                  key={tile.key}
                  data-active-tile={isActive ? "" : undefined}
                  onClick={tile.select}
                  sx={{
                    position: "relative",
                    flex: `0 0 ${tileBasis}`,
                    minWidth: 0,
                    height: TILE_HEIGHT,
                    borderRadius: 1,
                    overflow: "hidden",
                    cursor: "pointer",
                    bgcolor: "common.white",
                    boxSizing: "border-box",
                    border: isActive ? ACTIVE_BORDER : IDLE_BORDER,
                    "&:hover": {
                      border: isActive ? ACTIVE_BORDER : HOVER_BORDER,
                    },
                  }}
                >
                  {tile.indices.length > 1 ? (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        gridTemplateColumns: `repeat(${tile.indices.length}, 1fr)`,
                        gap: "2px",
                      }}
                    >
                      {tile.indices.map((index) => (
                        <Box
                          key={index}
                          sx={{
                            minWidth: 0,
                            backgroundImage: inView
                              ? `url(${srcFor(media[index], media[index].src)})`
                              : undefined,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <>
                      {renderMedia(tile.indices[0], false, inView)}
                      {item.type === "video" && (
                        <PlayCircle sx={playIconSx(20)} />
                      )}
                    </>
                  )}
                  {tileLabel && <Box sx={labelSx(true)}>{tileLabel}</Box>}
                </Box>
              );
            })}
          </Box>
          <Box
            onClick={() => (stripScrolls ? scrollStrip(1) : stepBy(1))}
            sx={arrowSx}
          >
            <ChevronRight fontSize="small" />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export const MediaGallery = forwardRef(MediaGalleryView);
